import { uniqBy } from 'lodash';
import React, { FC, useContext } from 'react';
import { useSelector } from 'react-redux';
import { Row } from 'react-styled-flexboxgrid';
import { useMedia } from 'react-use';

import {
  BUTTON_TYPES,
  media,
  NOTIFICATION_TYPES,
} from '@savgroup-front-common/constants';
import { Banner } from '@savgroup-front-common/core/atoms/Banners';
import { getCountryTranslationSelector } from '@savgroup-front-common/core/domains/i18n/selectors';
import { SafeFormattedMessageWithoutSpread } from '@savgroup-front-common/core/formatters';
import { BaseLoader } from '@savgroup-front-common/core/molecules/BaseLoader';
import { MyAccountIcon } from '@savgroup-front-common/core/protons/IconsNewDesign/MyAccount.icon';
import { ICONS_TYPE } from '@savgroup-front-common/types/Icon';
import { InitContext } from '@/view/app/NewLayout/InitProvider/InitProvider.context';

import {
  HandlingValues,
  PickupPointOption,
} from '../../../../../IrshPages.types';

import Address from './Address';
import { AdaptedPickupPointOption } from './components/MapWithControlledZoom/MapWithControlledZoom.types';
import PickupPointList from './components/PickupPointList';
import PickupPointMap from './components/PickupPointMap';
import messages from './messages';
import {
  $AddressContainer,
  $BackButtonContainer,
  $CloseButton,
  $ColListContainer,
  $ColMapContainer,
  $NoPickupPoint,
  $SelectContainerGrid,
  $SelectContainerRow,
} from './PickupPointSelector.styles';
import { AddressSelected } from './PickupPointSelector.types';

interface PickupPointSelectorProps {
  onClose: () => void;
  pickupPoints?: PickupPointOption[];
  maxDistance: number;
  onAddressSelected: (addressSelected: AddressSelected) => void;
  searchAddress?: AddressSelected;
  pickupPointsIsLoading: boolean;
  onSelectPickupPoint: (payload: PickupPointOption) => void;
  selectedPickupPoint?: PickupPointOption;
  handlingValues: HandlingValues;
  isImmediateDropAtStoreMethod: boolean;
}

const PickupPointSelector: FC<
  React.PropsWithChildren<PickupPointSelectorProps>
> = ({
  onClose,
  pickupPoints,
  maxDistance,
  onAddressSelected,
  searchAddress,
  pickupPointsIsLoading,
  onSelectPickupPoint,
  selectedPickupPoint,
  handlingValues,
  isImmediateDropAtStoreMethod,
}) => {
  const isMobileView = useMedia(media.maxWidth.xs);
  const { orders } = useContext(InitContext);

  const marketPlaceName = orders?.at(0)?.marketPlace;

  const cardRefs: any = {};

  const selectPickupPointOnMap = (
    selectedPickupPointOnMap: PickupPointOption,
  ) => {
    const cardElement = cardRefs[selectedPickupPointOnMap.value];

    if (cardElement) {
      cardElement.scrollIntoView();
    }
    onSelectPickupPoint(selectedPickupPointOnMap);
  };

  const filteredEnrichedPickupPoints = pickupPoints;

  const sortedPickupPointOptions = uniqBy(
    filteredEnrichedPickupPoints,
    'value',
  ).sort((a, b) => {
    const pickupPointA = a?.data?.pickupPoint;
    const pickupPointB = b?.data?.pickupPoint;

    return (
      (pickupPointA?.distanceInMeters ?? 0) -
      (pickupPointB?.distanceInMeters ?? 0)
    );
  });

  const handleSetSelectedPickupPoint = (
    pickupPointOption: AdaptedPickupPointOption,
  ) => {
    onSelectPickupPoint({
      value: pickupPointOption.value,
      label: pickupPointOption.label,
      data: {
        pickupPoint: pickupPointOption?.data?.pickupPoint,
        transportPlan: pickupPointOption?.data?.transportPlan,
      },
    });
  };

  const getCountry = useSelector(getCountryTranslationSelector);

  const isAllPickupPointsHaveNoGpsCordinates = sortedPickupPointOptions?.every(
    (pickupPointOption) => {
      const pickupPoint = pickupPointOption?.data?.pickupPoint;

      return !pickupPoint?.latitude && !pickupPoint?.longitude;
    },
  );

  const shouldShowPointMap =
    (!pickupPointsIsLoading || isMobileView) &&
    !isAllPickupPointsHaveNoGpsCordinates;

  return (
    <$SelectContainerGrid fluid>
      <$SelectContainerRow>
        <$ColListContainer sm={shouldShowPointMap ? 4 : 12}>
          <Row>
            <$BackButtonContainer xs={12}>
              <$CloseButton
                onClick={onClose}
                type={BUTTON_TYPES.BUTTON}
                icon={<MyAccountIcon icon={ICONS_TYPE.CROSS_ICON} />}
                primary
                naked
                small
              />
            </$BackButtonContainer>
          </Row>
          <$AddressContainer>
            <Address
              onAddressSelected={(addressSelected) => {
                onAddressSelected(addressSelected);
              }}
              searchAddress={searchAddress}
              maxDistance={maxDistance}
              getCountry={getCountry}
            />
          </$AddressContainer>

          {pickupPointsIsLoading && <BaseLoader />}

          {!pickupPointsIsLoading && (
            <Row>
              {pickupPoints && pickupPoints.length > 0 && (
                <PickupPointList
                  onSelectPickupPoint={handleSetSelectedPickupPoint}
                  pickupPoints={sortedPickupPointOptions}
                  handlingValues={handlingValues}
                  isImmediateDropAtStoreMethod={isImmediateDropAtStoreMethod}
                />
              )}

              {(!pickupPoints || pickupPoints?.length === 0) && (
                <$NoPickupPoint>
                  <Banner
                    isOpen
                    hollow
                    notificationType={NOTIFICATION_TYPES.ALERT}
                  >
                    <SafeFormattedMessageWithoutSpread
                      message={messages.noPickupPoints}
                    />
                  </Banner>
                </$NoPickupPoint>
              )}
            </Row>
          )}
        </$ColListContainer>

        {pickupPointsIsLoading && !isMobileView && <BaseLoader />}

        {shouldShowPointMap && (
          <$ColMapContainer sm={8}>
            <PickupPointMap
              searchAddress={searchAddress}
              selectedPickupPoint={selectedPickupPoint}
              onSelectPickupPoint={selectPickupPointOnMap}
              pickupPoints={sortedPickupPointOptions}
              marketPlaceName={marketPlaceName}
            />
          </$ColMapContainer>
        )}
      </$SelectContainerRow>
    </$SelectContainerGrid>
  );
};

export default PickupPointSelector;
